import { z } from "zod";

const jsonObjectSchema = z.record(z.string(), z.unknown());
const bigintStringSchema = z.string().regex(/^-?\d+$/);
const nullableBigintStringSchema = bigintStringSchema.nullable();
const nullableDateTimeSchema = z.string().datetime({ offset: true }).nullable();

const billingAccountWireSchema = z
  .object({
    calculated_at: z.string().datetime({ offset: true }),
    calculated_by: z.string().min(1),
    calculation_version: z.string().min(1),
    default_currency_code: z.string().length(3),
    metadata: jsonObjectSchema,
    organization_id: z.string().min(1),
    overall_remaining_basis_points: z
      .number()
      .int()
      .min(0)
      .max(10_000)
      .nullable(),
    revision: bigintStringSchema,
    source_batch_ref: z.string(),
    status: z.enum(["active", "suspended", "closed"]),
  })
  .strict();

const billingProductSummaryWireSchema = z
  .object({
    badge_text: z.string(),
    code: z.string().min(1),
    currency_code: z.string().length(3).nullable(),
    display_name: z.string().min(1),
    is_recommended: z.boolean(),
    list_price_minor: nullableBigintStringSchema,
    metadata: jsonObjectSchema,
    product_type: z.enum([
      "subscription_plan",
      "credit_topup",
      "storage_addon",
    ]),
    purchase_enabled: z.boolean(),
    service_period_count: z.number().int().positive().nullable(),
    service_period_unit: z
      .enum(["day", "month", "year", "one_time"])
      .nullable(),
    summary: z.string(),
    trial_days: z.number().int().nonnegative(),
  })
  .strict();

const billingSubscriptionWireSchema = z
  .object({
    cancel_at: nullableDateTimeSchema,
    effective_at: z.string().datetime({ offset: true }),
    ended_at: nullableDateTimeSchema,
    id: z.string().min(1),
    organization_id: z.string().min(1),
    period_ends_at: nullableDateTimeSchema,
    period_started_at: nullableDateTimeSchema,
    product: billingProductSummaryWireSchema,
    product_id: z.string().min(1),
    product_revision: bigintStringSchema,
    revision: bigintStringSchema,
    status: z.enum(["trialing", "active", "scheduled", "paused", "ended"]),
  })
  .strict();

const billingBalanceWireSchema = z
  .object({
    available_quantity: bigintStringSchema,
    balance_key: z.string().min(1),
    balance_type: z.enum([
      "plan_credit",
      "recharge_credit",
      "data_calls",
      "plan_storage",
      "storage_addon",
      "advertising_balance",
    ]),
    calculated_at: z.string().datetime({ offset: true }),
    calculated_by: z.string().min(1),
    calculation_version: z.string().min(1),
    currency_code: z.string().length(3).nullable(),
    display_name: z.string().min(1),
    effective_at: z.string().datetime({ offset: true }),
    expires_at: nullableDateTimeSchema,
    id: z.string().min(1),
    metadata: jsonObjectSchema,
    metric_key: z.string().min(1),
    product_id: z.string().min(1).nullable(),
    provider: z.string().min(1).nullable(),
    reserved_quantity: nullableBigintStringSchema,
    resource_id: z.string().min(1).nullable(),
    resource_type: z.string().min(1).nullable(),
    revision: bigintStringSchema,
    source_batch_ref: z.string(),
    status: z.enum(["active", "expired", "suspended"]),
    total_quantity: nullableBigintStringSchema,
    unit: z.string().min(1),
    used_quantity: nullableBigintStringSchema,
  })
  .strict();

export const billingOverviewResponseSchema = z
  .object({
    account: billingAccountWireSchema.nullable(),
    balances: z.array(billingBalanceWireSchema),
    current_subscription: billingSubscriptionWireSchema.nullable(),
    organization_id: z.string().min(1),
  })
  .strict()
  .transform((response) => ({
    account: response.account
      ? {
          calculatedAt: response.account.calculated_at,
          calculatedBy: response.account.calculated_by,
          calculationVersion: response.account.calculation_version,
          defaultCurrencyCode: response.account.default_currency_code,
          metadata: response.account.metadata,
          organizationId: response.account.organization_id,
          overallRemainingBasisPoints:
            response.account.overall_remaining_basis_points,
          revision: response.account.revision,
          sourceBatchRef: response.account.source_batch_ref,
          status: response.account.status,
        }
      : null,
    balances: response.balances.map((balance) => ({
      availableQuantity: balance.available_quantity,
      balanceKey: balance.balance_key,
      balanceType: balance.balance_type,
      calculatedAt: balance.calculated_at,
      calculatedBy: balance.calculated_by,
      calculationVersion: balance.calculation_version,
      currencyCode: balance.currency_code,
      displayName: balance.display_name,
      effectiveAt: balance.effective_at,
      expiresAt: balance.expires_at,
      id: balance.id,
      metadata: balance.metadata,
      metricKey: balance.metric_key,
      productId: balance.product_id,
      provider: balance.provider,
      reservedQuantity: balance.reserved_quantity,
      resourceId: balance.resource_id,
      resourceType: balance.resource_type,
      revision: balance.revision,
      sourceBatchRef: balance.source_batch_ref,
      status: balance.status,
      totalQuantity: balance.total_quantity,
      unit: balance.unit,
      usedQuantity: balance.used_quantity,
    })),
    currentSubscription: response.current_subscription
      ? {
          cancelAt: response.current_subscription.cancel_at,
          effectiveAt: response.current_subscription.effective_at,
          endedAt: response.current_subscription.ended_at,
          id: response.current_subscription.id,
          organizationId: response.current_subscription.organization_id,
          periodEndsAt: response.current_subscription.period_ends_at,
          periodStartedAt: response.current_subscription.period_started_at,
          product: transformProductSummary(
            response.current_subscription.product,
          ),
          productId: response.current_subscription.product_id,
          productRevision: response.current_subscription.product_revision,
          revision: response.current_subscription.revision,
          status: response.current_subscription.status,
        }
      : null,
    organizationId: response.organization_id,
  }));

const billingEntitlementWireSchema = z
  .object({
    display_name: z.string().min(1),
    entitlement_key: z.string().min(1),
    id: z.string().min(1),
    metadata: jsonObjectSchema,
    metric_key: z.string().min(1),
    quantity: bigintStringSchema,
    sort_order: z.number().int(),
    unit: z.string().min(1),
  })
  .strict();

const billingProductWireSchema = billingProductSummaryWireSchema.extend({
  entitlements: z.array(billingEntitlementWireSchema),
  id: z.string().min(1),
  revision: bigintStringSchema,
  sort_order: z.number().int(),
});

export const billingProductsResponseSchema = z
  .object({ products: z.array(billingProductWireSchema) })
  .strict()
  .transform((response) => ({
    products: response.products.map((product) => ({
      ...transformProductSummary(product),
      entitlements: product.entitlements.map((entitlement) => ({
        displayName: entitlement.display_name,
        entitlementKey: entitlement.entitlement_key,
        id: entitlement.id,
        metadata: entitlement.metadata,
        metricKey: entitlement.metric_key,
        quantity: entitlement.quantity,
        sortOrder: entitlement.sort_order,
        unit: entitlement.unit,
      })),
      id: product.id,
      revision: product.revision,
      sortOrder: product.sort_order,
    })),
  }));

const billingTransactionWireSchema = z
  .object({
    amount_minor: nullableBigintStringSchema,
    currency_code: z.string().length(3).nullable(),
    description: z.string(),
    id: z.string().min(1),
    metadata: jsonObjectSchema,
    occurred_at: z.string().datetime({ offset: true }),
    operation_label: z.string().min(1),
    order_id: z.string().min(1).nullable(),
    revision: bigintStringSchema,
    source_type: z.string().min(1),
    status: z.enum(["processing", "paid", "failed", "cancelled", "refunded"]),
    transaction_number: z.string().min(1),
    transaction_type: z.string().min(1),
  })
  .strict();

const billingOrderSchema = z
  .object({
    id: z.string().min(1),
    order_number: z.string().min(1),
    product_id: z.string().nullable(),
    order_type: z.enum(["subscription", "credit_topup", "storage_addon"]),
    status: z.enum(["pending", "paid", "cancelled", "failed", "refunded"]),
    currency_code: z.string().length(3),
    subtotal_minor: bigintStringSchema,
    discount_minor: bigintStringSchema,
    tax_minor: bigintStringSchema,
    payable_minor: bigintStringSchema,
    paid_minor: bigintStringSchema,
    promotion_code: z.string(),
    payment_channel: z.string(),
    effective_at: nullableDateTimeSchema,
    expires_at: nullableDateTimeSchema,
    paid_at: nullableDateTimeSchema,
    created_at: z.string().datetime({ offset: true }),
    revision: bigintStringSchema,
    metadata: jsonObjectSchema,
    product: z
      .object({
        code: z.string().nullable(),
        product_type: z.string().nullable(),
        display_name: z.string().nullable(),
        summary: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export const billingOrderResponseSchema = z
  .object({ order: billingOrderSchema })
  .strict();
export const billingOrdersResponseSchema = z
  .object({
    orders: z.array(billingOrderSchema),
    next_cursor: z.string().nullable(),
  })
  .strict();
export const mockPurchaseResponseSchema = z
  .object({ order: billingOrderSchema, duplicate: z.boolean() })
  .strict();
export const mockPurchasePreviewSchema = z
  .object({
    amount_minor: bigintStringSchema,
    currency_code: z.string().length(3),
    grants: z.array(
      z
        .object({
          display_name: z.string(),
          quantity: bigintStringSchema,
          unit: z.string(),
        })
        .strict(),
    ),
  })
  .strict();
export type BillingOrder = z.infer<typeof billingOrderSchema>;

export const billingTransactionsResponseSchema = z
  .object({
    next_cursor: z.string().min(1).nullable(),
    transactions: z.array(billingTransactionWireSchema),
  })
  .strict()
  .transform((response) => ({
    nextCursor: response.next_cursor,
    transactions: response.transactions.map((transaction) => ({
      amountMinor: transaction.amount_minor,
      currencyCode: transaction.currency_code,
      description: transaction.description,
      id: transaction.id,
      metadata: transaction.metadata,
      occurredAt: transaction.occurred_at,
      operationLabel: transaction.operation_label,
      orderId: transaction.order_id,
      revision: transaction.revision,
      sourceType: transaction.source_type,
      status: transaction.status,
      transactionNumber: transaction.transaction_number,
      transactionType: transaction.transaction_type,
    })),
  }));

const advertisingAccountWireSchema = z
  .object({
    agency_organization_id: z.string().min(1),
    balance: z
      .object({
        available_amount_minor: bigintStringSchema,
        calculated_at: nullableDateTimeSchema,
        currency_code: z.string().length(3).nullable(),
      })
      .strict(),
    display_name: z.string().min(1),
    external_ad_account_id: z.string().min(1),
    id: z.string().min(1),
    provider: z.string().min(1),
    status: z.literal("active"),
  })
  .strict();

export const advertisingAccountsResponseSchema = z
  .object({ accounts: z.array(advertisingAccountWireSchema) })
  .strict()
  .transform((response) => ({
    accounts: response.accounts.map((account) => ({
      agencyOrganizationId: account.agency_organization_id,
      balance: {
        availableAmountMinor: account.balance.available_amount_minor,
        calculatedAt: account.balance.calculated_at,
        currencyCode: account.balance.currency_code,
      },
      displayName: account.display_name,
      externalAdAccountId: account.external_ad_account_id,
      id: account.id,
      provider: account.provider,
      status: account.status,
    })),
  }));

const advertisingCurrencyWireSchema = z
  .object({
    currency_code: z.string().length(3),
    display_name: z.string().min(1),
    id: z.string().min(1),
    maximum_amount_minor: nullableBigintStringSchema,
    minimum_amount_minor: nullableBigintStringSchema,
    revision: bigintStringSchema,
    sort_order: z.number().int(),
    symbol: z.string(),
  })
  .strict();

const advertisingPaymentMethodWireSchema = z
  .object({
    display_name: z.string().min(1),
    id: z.string().min(1),
    logo_url: z.string(),
    method_key: z.string().min(1),
    method_type: z.enum(["bank_transfer", "wallet", "bank_account"]),
    profile: z
      .object({
        currency_code: z.string().length(3),
        display_name: z.string().min(1),
        id: z.string().min(1),
        instructions: z.string(),
        revision: bigintStringSchema,
        sort_order: z.number().int(),
      })
      .strict(),
    revision: bigintStringSchema,
    sort_order: z.number().int(),
    summary: z.string(),
  })
  .strict();

export const advertisingRechargeOptionsResponseSchema = z
  .object({
    ad_account_id: z.string().min(1),
    currencies: z.array(advertisingCurrencyWireSchema),
    payment_methods: z.array(advertisingPaymentMethodWireSchema),
    presets: z.array(
      z
        .object({
          amount_minor: bigintStringSchema,
          currency_id: z.string().min(1),
          display_label: z.string(),
          id: z.string().min(1),
          sort_order: z.number().int(),
        })
        .strict(),
    ),
  })
  .strict()
  .transform((response) => ({
    adAccountId: response.ad_account_id,
    currencies: response.currencies.map((currency) => ({
      currencyCode: currency.currency_code,
      displayName: currency.display_name,
      id: currency.id,
      maximumAmountMinor: currency.maximum_amount_minor,
      minimumAmountMinor: currency.minimum_amount_minor,
      revision: currency.revision,
      sortOrder: currency.sort_order,
      symbol: currency.symbol,
    })),
    paymentMethods: response.payment_methods.map((method) => ({
      displayName: method.display_name,
      id: method.id,
      logoUrl: method.logo_url,
      methodKey: method.method_key,
      methodType: method.method_type,
      profile: {
        currencyCode: method.profile.currency_code,
        displayName: method.profile.display_name,
        id: method.profile.id,
        instructions: method.profile.instructions,
        revision: method.profile.revision,
        sortOrder: method.profile.sort_order,
      },
      revision: method.revision,
      sortOrder: method.sort_order,
      summary: method.summary,
    })),
    presets: response.presets.map((preset) => ({
      amountMinor: preset.amount_minor,
      currencyId: preset.currency_id,
      displayLabel: preset.display_label,
      id: preset.id,
      sortOrder: preset.sort_order,
    })),
  }));

export const advertisingPaymentProfileResponseSchema = z
  .object({
    profile: z
      .object({
        currency_code: z.string().length(3),
        display_name: z.string().min(1),
        fields: z.array(
          z
            .object({
              copyable: z.boolean(),
              field_key: z.string().min(1),
              field_label: z.string().min(1),
              field_value: z.string(),
              id: z.string().min(1),
              metadata: jsonObjectSchema,
              sort_order: z.number().int(),
            })
            .strict(),
        ),
        id: z.string().min(1),
        instructions: z.string(),
        method: z
          .object({
            display_name: z.string().min(1),
            logo_url: z.string(),
            method_key: z.string().min(1),
            method_type: z.enum(["bank_transfer", "wallet", "bank_account"]),
            revision: bigintStringSchema,
            summary: z.string(),
          })
          .strict(),
        payment_method_id: z.string().min(1),
        revision: bigintStringSchema,
      })
      .strict(),
  })
  .strict()
  .transform((response) => ({
    profile: {
      currencyCode: response.profile.currency_code,
      displayName: response.profile.display_name,
      fields: response.profile.fields.map((field) => ({
        copyable: field.copyable,
        fieldKey: field.field_key,
        fieldLabel: field.field_label,
        fieldValue: field.field_value,
        id: field.id,
        metadata: field.metadata,
        sortOrder: field.sort_order,
      })),
      id: response.profile.id,
      instructions: response.profile.instructions,
      method: {
        displayName: response.profile.method.display_name,
        logoUrl: response.profile.method.logo_url,
        methodKey: response.profile.method.method_key,
        methodType: response.profile.method.method_type,
        revision: response.profile.method.revision,
        summary: response.profile.method.summary,
      },
      paymentMethodId: response.profile.payment_method_id,
      revision: response.profile.revision,
    },
  }));

const advertisingQuoteRequestWireSchema = z
  .object({
    ad_account_id: z.string().min(1),
    created_at: z.string().datetime({ offset: true }),
    failure_code: z.string(),
    id: z.string().min(1),
    payment_profile_id: z.string().min(1),
    requested_amount_minor: bigintStringSchema,
    requested_currency_code: z.string().length(3),
    status: z.enum(["pending", "ready", "failed", "expired", "cancelled"]),
    updated_at: z.string().datetime({ offset: true }),
  })
  .strict();

export const advertisingQuoteRequestResponseSchema = z
  .object({ quote_request: advertisingQuoteRequestWireSchema })
  .strict()
  .transform((response) => ({
    quoteRequest: transformAdvertisingQuoteRequest(response.quote_request),
  }));

const advertisingQuoteWireSchema = z
  .object({
    calculated_by: z.string().min(1),
    calculation_version: z.string().min(1),
    credit_currency_code: z.string().length(3),
    credited_amount_minor: bigintStringSchema,
    exchange_rate_text: z.string(),
    expires_at: z.string().datetime({ offset: true }),
    fee_amount_minor: bigintStringSchema,
    id: z.string().min(1),
    metadata: jsonObjectSchema,
    payable_amount_minor: bigintStringSchema,
    quote_number: z.string().min(1),
    revision: bigintStringSchema,
    settlement_currency_code: z.string().length(3),
    status: z.enum(["ready", "expired", "cancelled", "failed"]),
  })
  .strict();

export const advertisingQuoteResultResponseSchema = z
  .object({
    quote: advertisingQuoteWireSchema.nullable(),
    quote_request: advertisingQuoteRequestWireSchema,
  })
  .strict()
  .transform((response) => ({
    quote: response.quote
      ? {
          calculatedBy: response.quote.calculated_by,
          calculationVersion: response.quote.calculation_version,
          creditCurrencyCode: response.quote.credit_currency_code,
          creditedAmountMinor: response.quote.credited_amount_minor,
          exchangeRateText: response.quote.exchange_rate_text,
          expiresAt: response.quote.expires_at,
          feeAmountMinor: response.quote.fee_amount_minor,
          id: response.quote.id,
          metadata: response.quote.metadata,
          payableAmountMinor: response.quote.payable_amount_minor,
          quoteNumber: response.quote.quote_number,
          revision: response.quote.revision,
          settlementCurrencyCode: response.quote.settlement_currency_code,
          status: response.quote.status,
        }
      : null,
    quoteRequest: transformAdvertisingQuoteRequest(response.quote_request),
  }));

export const advertisingRechargeRequestResponseSchema = z
  .object({
    recharge_request: z
      .object({
        ad_account_id: z.string().min(1),
        created_at: z.string().datetime({ offset: true }),
        credit_currency_code: z.string().length(3),
        credited_amount_minor: bigintStringSchema,
        id: z.string().min(1),
        payable_amount_minor: bigintStringSchema,
        quote_id: z.string().min(1),
        remitted_at: z.string().datetime({ offset: true }),
        request_number: z.string().min(1),
        requested_amount_minor: bigintStringSchema,
        requested_currency_code: z.string().length(3),
        settlement_currency_code: z.string().length(3),
        status: z.enum([
          "submitted",
          "reviewing",
          "approved",
          "rejected",
          "credited",
          "cancelled",
        ]),
      })
      .strict(),
  })
  .strict()
  .transform((response) => ({
    rechargeRequest: {
      adAccountId: response.recharge_request.ad_account_id,
      createdAt: response.recharge_request.created_at,
      creditCurrencyCode: response.recharge_request.credit_currency_code,
      creditedAmountMinor: response.recharge_request.credited_amount_minor,
      id: response.recharge_request.id,
      payableAmountMinor: response.recharge_request.payable_amount_minor,
      quoteId: response.recharge_request.quote_id,
      remittedAt: response.recharge_request.remitted_at,
      requestNumber: response.recharge_request.request_number,
      requestedAmountMinor: response.recharge_request.requested_amount_minor,
      requestedCurrencyCode: response.recharge_request.requested_currency_code,
      settlementCurrencyCode:
        response.recharge_request.settlement_currency_code,
      status: response.recharge_request.status,
    },
  }));

const advertisingActivityTransactionWireSchema =
  billingTransactionWireSchema.extend({
    provider: z.string().min(1).nullable(),
    resource_id: z.string().min(1).nullable(),
    resource_type: z.string().min(1).nullable(),
  });

export const advertisingAccountActivityResponseSchema = z
  .object({
    ad_account_id: z.string().min(1),
    daily_snapshots: z.array(
      z
        .object({
          consumed_amount_minor: bigintStringSchema,
          currency_code: z.string().length(3),
          date: z.string().date(),
          ending_balance_minor: nullableBigintStringSchema,
          revision: bigintStringSchema,
        })
        .strict(),
    ),
    month: z.string().regex(/^\d{4}-\d{2}$/),
    transactions: z.array(advertisingActivityTransactionWireSchema),
  })
  .strict()
  .transform((response) => ({
    adAccountId: response.ad_account_id,
    dailySnapshots: response.daily_snapshots.map((snapshot) => ({
      consumedAmountMinor: snapshot.consumed_amount_minor,
      currencyCode: snapshot.currency_code,
      date: snapshot.date,
      endingBalanceMinor: snapshot.ending_balance_minor,
      revision: snapshot.revision,
    })),
    month: response.month,
    transactions: response.transactions.map((transaction) => ({
      amountMinor: transaction.amount_minor,
      currencyCode: transaction.currency_code,
      description: transaction.description,
      id: transaction.id,
      metadata: transaction.metadata,
      occurredAt: transaction.occurred_at,
      operationLabel: transaction.operation_label,
      orderId: transaction.order_id,
      provider: transaction.provider,
      resourceId: transaction.resource_id,
      resourceType: transaction.resource_type,
      revision: transaction.revision,
      sourceType: transaction.source_type,
      status: transaction.status,
      transactionNumber: transaction.transaction_number,
      transactionType: transaction.transaction_type,
    })),
  }));

function transformAdvertisingQuoteRequest(
  request: z.infer<typeof advertisingQuoteRequestWireSchema>,
) {
  return {
    adAccountId: request.ad_account_id,
    createdAt: request.created_at,
    failureCode: request.failure_code,
    id: request.id,
    paymentProfileId: request.payment_profile_id,
    requestedAmountMinor: request.requested_amount_minor,
    requestedCurrencyCode: request.requested_currency_code,
    status: request.status,
    updatedAt: request.updated_at,
  };
}

function transformProductSummary(
  product: z.infer<typeof billingProductSummaryWireSchema>,
) {
  return {
    badgeText: product.badge_text,
    code: product.code,
    currencyCode: product.currency_code,
    displayName: product.display_name,
    isRecommended: product.is_recommended,
    listPriceMinor: product.list_price_minor,
    metadata: product.metadata,
    productType: product.product_type,
    purchaseEnabled: product.purchase_enabled,
    servicePeriodCount: product.service_period_count,
    servicePeriodUnit: product.service_period_unit,
    summary: product.summary,
    trialDays: product.trial_days,
  };
}

export type BillingOverview = ReturnType<
  typeof billingOverviewResponseSchema.parse
>;
export type BillingBalance = BillingOverview["balances"][number];
export type BillingProduct = ReturnType<
  typeof billingProductsResponseSchema.parse
>["products"][number];
export type BillingTransaction = ReturnType<
  typeof billingTransactionsResponseSchema.parse
>["transactions"][number];
export type BillingProductType = BillingProduct["productType"];
export type AdvertisingAccount = ReturnType<
  typeof advertisingAccountsResponseSchema.parse
>["accounts"][number];
export type AdvertisingRechargeOptions = ReturnType<
  typeof advertisingRechargeOptionsResponseSchema.parse
>;
export type AdvertisingPaymentProfile = ReturnType<
  typeof advertisingPaymentProfileResponseSchema.parse
>["profile"];
export type AdvertisingQuoteResult = ReturnType<
  typeof advertisingQuoteResultResponseSchema.parse
>;
export type AdvertisingAccountActivity = ReturnType<
  typeof advertisingAccountActivityResponseSchema.parse
>;
